/**
 * Props — the reef. Brain coral, staghorn, sea fans and boulders, every one of them grown
 * from a seed and drawn as one InstancedMesh per variant.
 *
 * Two vendored generators, no voxels. Coral is grown as smooth curvature and thin plates;
 * rock is CUT — a cube sliced by planes drawn from three cleavage axes, so its faces are
 * genuinely flat and its edges genuinely hard. Neither shape survives a voxel grid at any
 * resolution this frame budget allows, which is why the props left the creature pipeline.
 *
 * Placement is zonal AND STREAMED. The coast is endless along X, so props cannot be a fixed
 * scatter built once: the world is diced into along-shore cells, each seeded from (world
 * seed, cell index) so a cell always regenerates identically, and a window of cells around
 * the player is kept live. Walk away and back and the same reef is there.
 *
 * Where the rock is at all lives in Coast.reefMask, which the seafloor shader displaces and
 * colours from too.
 */
import * as THREE from 'three';
import { Rng } from '../core/Rng.ts';
import { SharkMaterial } from '../render/SharkMaterial.ts';
import { bedHeight, bedNormal, reefMask, waterDepth } from './Coast.ts';
import { build as buildCoral, type CoralSpecies } from './coral/proceduralCoral.js';
import { build as buildRock, PALETTES as ROCK_PALETTES, type RockKind, type RockAttach } from './rock/seaRocks.js';
import type { SkyEnv } from './DayNight.ts';

type PropKind = 'coral' | 'rock';

interface Variant {
  kind: PropKind;
  geometry: THREE.BufferGeometry;
  /** world height, used to seat the model on the bed */
  height: number;
  /** which growth form this is, so depth and aspect can select it; null for rock */
  coralKind: CoralSpecies | null;
  /** rock only: outward-facing points on unburied upward faces, where coral can settle */
  attach: RockAttach[] | null;
}

/**
 * Coral species and how many distinct colonies to grow of each. Detail is deliberately low:
 * the generator's default (1.0) is 5-14k triangles per colony, and at ~45 colonies that
 * alone would outweigh the entire rest of the scene.
 */
const CORAL_SET: Array<[CoralSpecies, number]> = [
  ['branching', 2],
  ['table', 2],
  ['brain', 2],
  ['massive', 2],
  ['foliose', 2],
  ['fan', 1],
];

/* Every tessellation term in the generator is `max(floor, round(n * detail))`, so at 0.45
   almost all of them sat on their floor and the whole roster was built at minimum quality —
   sea-fan rods came out as square tubes. 0.75 is where the floors stop binding. */
const CORAL_DETAIL = 0.75;

/* The generator bakes AO into vertex colour at 0.35-0.62 by species. That was authored for a
   flat preview; here the colony is already shaded by a directional key, an ambient gradient
   and a shadow map, so the baked term lands on top of real lighting and the crevices go
   black. Scale it back rather than off — a little contact darkening still helps the read. */
const CORAL_AO = 0.45;

/**
 * Per-species stand shape. A staghorn thicket packs tight and low; plates need room and a
 * neighbour's plate over them is a dead colony.
 */
const CORAL_STAND: Record<CoralSpecies, { spacing: number; scale: [number, number]; density: number }> = {
  branching: { spacing: 1.6, scale: [1.30, 2.10], density: 0.95 },
  table:     { spacing: 3.4, scale: [1.40, 2.20], density: 0.68 },
  brain:     { spacing: 2.6, scale: [0.90, 1.50], density: 0.78 },
  massive:   { spacing: 3.0, scale: [1.00, 1.70], density: 0.74 },
  foliose:   { spacing: 2.0, scale: [1.10, 1.80], density: 0.86 },
  fan:       { spacing: 3.4, scale: [2.20, 3.40], density: 0.42 },
};

/** mask value above which a stand may be founded; below it, sand */
const REEF_ON = 0.55;

/**
 * Reef stone.
 *
 * Registered into the generator's own palette table rather than passed in — it takes a KEY,
 * not an object, and the table is exported and mutable, so this is the seam that does not
 * require editing the vendored file.
 *
 * Authored in display space like every other generator here (see the sRGB conversion in
 * addVariant). That makes them read numerically brighter than the generator's defaults while
 * landing DARKER on screen once converted: a 0.40 here is 0.13 linear. Reef rock is dark
 * against bright sand, and that contrast is what makes a patch read as a place.
 */
ROCK_PALETTES.reefDark = {
  dark: [0.170, 0.180, 0.190], mid: [0.300, 0.320, 0.340], lit: [0.480, 0.500, 0.520],
};
ROCK_PALETTES.reefCarbonate = {
  dark: [0.200, 0.198, 0.186], mid: [0.365, 0.358, 0.334], lit: [0.560, 0.548, 0.516],
};
ROCK_PALETTES.reefIron = {
  dark: [0.210, 0.178, 0.148], mid: [0.372, 0.312, 0.252], lit: [0.548, 0.468, 0.376],
};

/* The roster of stone. `outcrop` is the workhorse — a low jagged ridge is what a rocky patch
   actually looks like — with `stack` for ledges and `rubble` for the skirt of broken material
   every formation sheds. No spires: a spire is tall and narrow, which is the opposite of what
   a coral formation is, and a field of them reads as a graveyard. */
const ROCK_SET: Array<{ kind: RockKind; palette: string; count: number }> = [
  { kind: 'outcrop', palette: 'reefCarbonate', count: 3 },
  { kind: 'outcrop', palette: 'reefDark', count: 3 },
  { kind: 'outcrop', palette: 'reefIron', count: 1 },
  { kind: 'stack', palette: 'reefCarbonate', count: 2 },
  { kind: 'stack', palette: 'reefDark', count: 1 },
  { kind: 'boulder', palette: 'reefDark', count: 1 },
  { kind: 'rubble', palette: 'reefIron', count: 2 },
  { kind: 'rubble', palette: 'reefCarbonate', count: 1 },
];

/**
 * Which growth forms will settle on a rock face, and how big relative to the rock.
 *
 * Encrusting and massive forms take vertical and overhanging rock; a table needs to be near
 * horizontal or it is a plate leaning on nothing; a gorgonian wants an edge with water moving
 * past it. `up` is the minimum face normal Y the form will accept.
 */
const CORAL_ON_ROCK: Record<CoralSpecies, { up: number; scale: number; weight: number }> = {
  branching: { up: 0.30, scale: 0.55, weight: 1.20 },
  massive:   { up: 0.24, scale: 0.50, weight: 1.10 },
  brain:     { up: 0.34, scale: 0.48, weight: 0.90 },
  foliose:   { up: 0.24, scale: 0.52, weight: 0.85 },
  table:     { up: 0.62, scale: 0.60, weight: 0.70 },
  // A gorgonian wants a firm near-level ledge, and it is both the rarest form on a real
  // reef and by some way the most expensive geometry here. Rare on both counts.
  fan:       { up: 0.48, scale: 0.75, weight: 0.12 },
};

function sstep(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/** along-shore width of one streaming cell, u */
const CELL = 64;
/** cells kept live either side of the player's cell */
const CELL_RADIUS = 3;
/** per-variant instance capacity; the window never comes close, and overflow is dropped */
const CAP = 512;
/** cross-shore band props may occupy: from the tide line to the top of the fore-reef */
const Z_MIN = 2;
const Z_MAX = 128;

export class Props {
  readonly group = new THREE.Group();

  private readonly variants: Variant[] = [];
  private readonly meshes: THREE.InstancedMesh[] = [];
  private readonly materials: SharkMaterial[] = [];
  private readonly seed: number;
  /** cell index -> per-variant matrices. The live window is the union of these. */
  private readonly cells = new Map<number, THREE.Matrix4[][]>();
  private centre = Number.NaN;

  constructor(seed = 0xc0a15) {
    this.seed = seed >>> 0;
    const rng = new Rng(this.seed);
    this.group.name = 'reef';

    /**
     * Hand a generator mesh to the GPU. Both generators emit the same shape, so one path
     * covers coral and rock; the only fork is the index buffer, which the rock deliberately
     * has none of — sharing vertices between faces is exactly what would round its creases
     * back off.
     */
    const addVariant = (
      kind: PropKind,
      m: { positions: Float32Array; normals: Float32Array; colors: Float32Array;
           indices: Uint32Array | null; verts: number; bounds: { h: number };
           attach?: RockAttach[] },
      coralKind: CoralSpecies | null,
    ): void => {
      // Both generators author pigment in display space (their previews assumed a plain lit
      // material with no colour management). This pipeline shades in LINEAR with ACES on the
      // end, so handing those values over untouched makes every colony a chalky near-white —
      // a 0.72 tan reads as 0.72 linear, roughly twice its intended intensity.
      const col = m.colors;
      for (let i = 0; i < col.length; i++) {
        const c = col[i];
        col[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(m.positions, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(m.normals, 3));
      g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      // The shark material reads spineT for its swim deform. Nothing here moves, but the
      // attribute has to exist or the prop inherits whatever was last bound.
      g.setAttribute('spineT', new THREE.BufferAttribute(new Float32Array(m.verts), 1));
      if (m.indices) g.setIndex(new THREE.BufferAttribute(m.indices, 1));
      g.computeBoundingSphere();
      this.variants.push({ kind, geometry: g, height: m.bounds.h, coralKind, attach: m.attach ?? null });
    };

    for (const R of ROCK_SET) {
      for (let v = 0; v < R.count; v++) {
        addVariant('rock', buildRock({
          kind: R.kind,
          seed: rng.int(1, 1 << 28),
          size: 1,
          palette: R.palette,
          // Turf only on the tops, and not much: heavy algae on every face flattens the
          // faceting back out, which is the one thing this generator is here for.
          algae: rng.range(0.30, 0.62),
        }), null);
      }
    }

    for (const [species, count] of CORAL_SET) {
      for (let v = 0; v < count; v++) {
        addVariant('coral', buildCoral({
          species,
          seed: rng.int(1, 1 << 28),
          size: 1,
          detail: CORAL_DETAIL,
          ao: CORAL_AO,
          // A little bleaching variation; full pigment on every colony reads as plastic.
          bleach: rng.next() < 0.18 ? rng.range(0.15, 0.5) : 0,
        }), species);
      }
    }

    for (let i = 0; i < this.variants.length; i++) {
      const variant = this.variants[i];
      const material = new SharkMaterial({ instanced: true });
      material.setSwim(0, 0);
      this.materials.push(material);

      const inst = new THREE.InstancedMesh(variant.geometry, material, CAP);
      inst.count = 0;
      // The window slides with the player and every prop is inside it by construction, so
      // per-mesh culling would only ever cost a bounds test it cannot win.
      inst.frustumCulled = false;
      inst.name = `prop-${variant.kind}-${i}`;
      variant.geometry.setAttribute('aPhase', new THREE.InstancedBufferAttribute(new Float32Array(CAP), 1));
      this.meshes.push(inst);
      this.group.add(inst);
    }

    this.setCentre(0);
  }

  get count(): number {
    return this.meshes.reduce((a, m) => a + m.count, 0);
  }

  update(time: number, playerX: number): void {
    for (const mat of this.materials) mat.setTime(time);
    const ci = Math.floor(playerX / CELL);
    if (ci !== this.centre) this.setCentre(ci);
  }

  /** the reef receives as well as casts: the shark's shadow has to cross the coral */
  setEnv(env: SkyEnv): void {
    for (const m of this.materials) m.setEnv(env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    for (const mat of this.materials) mat.setShadow(map, matrix, strength);
  }

  /** slide the live window, generating only the cells that just came into range */
  private setCentre(ci: number): void {
    this.centre = ci;
    for (const k of Array.from(this.cells.keys())) {
      if (Math.abs(k - ci) > CELL_RADIUS) this.cells.delete(k);
    }
    for (let k = ci - CELL_RADIUS; k <= ci + CELL_RADIUS; k++) {
      if (!this.cells.has(k)) this.cells.set(k, this.buildCell(k));
    }

    for (let i = 0; i < this.meshes.length; i++) {
      const mesh = this.meshes[i];
      let n = 0;
      for (const per of this.cells.values()) {
        const list = per[i];
        for (let j = 0; j < list.length && n < CAP; j++) mesh.setMatrixAt(n++, list[j]);
      }
      mesh.count = n;
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /**
   * Everything in one along-shore cell, from a stream seeded by the cell index.
   *
   * Deterministic per cell is the whole contract: nothing here may read the player's
   * position, the frame, or any state outside (seed, ci), or a reef would rearrange itself
   * behind your back the moment you swam away and returned.
   */
  private buildCell(ci: number): THREE.Matrix4[][] {
    const rng = new Rng((Math.imul(this.seed ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(ci, 0xc2b2ae35)) >>> 0);
    const out: THREE.Matrix4[][] = this.variants.map(() => []);
    const x0 = ci * CELL;

    const m = new THREE.Matrix4();
    const pos = new THREE.Vector3();
    const scl = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0);
    const yawQ = new THREE.Quaternion();
    const nrm = { x: 0, y: 1, z: 0 };
    const tilt = new THREE.Vector3();
    const lp = new THREE.Vector3();
    const ln = new THREE.Vector3();

    const pushInstance = (vi: number, px: number, py: number, pz: number, rot: THREE.Quaternion, s: number): void => {
      pos.set(px, py, pz);
      scl.setScalar(s);
      out[vi].push(m.clone().compose(pos, rot, scl));
    };

    /**
     * Seat a prop on the bed and return the pose, so a caller can hang things off it. The
     * lean follows the bed's own slope — nothing stands plumb on a dune — except a gorgonian,
     * which holds itself upright across the current.
     */
    const seat = (vi: number, x: number, z: number, s: number): { rot: THREE.Quaternion; y: number } => {
      bedNormal(x, z, nrm);
      const k = this.variants[vi].coralKind === 'fan' ? 0.15 : 0.45;
      tilt.set(nrm.x * k, 1 - k + nrm.y * k, nrm.z * k).normalize();
      const rot = new THREE.Quaternion().setFromUnitVectors(up, tilt);
      yawQ.setFromAxisAngle(up, rng.range(0, Math.PI * 2));
      rot.multiply(yawQ);
      // Everything both generators make is based at y = 0, so it only needs sinking a touch
      // into the substrate to kill the seam.
      const y = bedHeight(x, z) - 0.12 * s;
      pushInstance(vi, x, y, z, rot, s);
      return { rot, y };
    };

    const kinded = (k: PropKind): number[] =>
      this.variants.map((v, i) => (v.kind === k ? i : -1)).filter((i) => i >= 0);
    const rockVi = kinded('rock');
    const anyOf = (list: number[]): number => list[rng.int(0, list.length - 1)];

    const bySpecies = new Map<CoralSpecies, number[]>();
    this.variants.forEach((v, i) => {
      if (v.kind !== 'coral') return;
      const k = v.coralKind as CoralSpecies;
      const list = bySpecies.get(k);
      if (list) list.push(i);
      else bySpecies.set(k, [i]);
    });
    const pickWeighted = <T,>(table: Array<[T, number]>): T => {
      let total = 0;
      for (const t of table) total += t[1];
      let r = rng.next() * total;
      for (const [value, w] of table) {
        r -= w;
        if (r <= 0) return value;
      }
      return table[table.length - 1][0];
    };

    /** the largest-first, minimum-gap pick from the generator's own attachPoints() */
    const pickAttach = (all: RockAttach[], want: number, gap: number): RockAttach[] => {
      const picked: RockAttach[] = [];
      for (const a of all) {
        if (picked.length >= want) break;
        let ok = true;
        for (const b of picked) {
          if (Math.hypot(a.p[0] - b.p[0], a.p[1] - b.p[1], a.p[2] - b.p[2]) < gap) { ok = false; break; }
        }
        if (ok) picked.push(a);
      }
      return picked;
    };

    /** settle colonies onto the faces of a rock that has just been placed */
    const colonise = (rvi: number, rx: number, rz: number, rs: number, rot: THREE.Quaternion, ry: number): void => {
      const all = this.variants[rvi].attach;
      if (!all || all.length === 0) return;
      // Local-space gap, so the spacing scales with the rock rather than thinning out on big
      // ones and crowding on small ones.
      for (const a of pickAttach(all, 1 + rng.int(0, 3), 0.30)) {
        if (rng.next() < 0.26) continue;                    // bare rock has to stay possible
        const table: Array<[CoralSpecies, number]> = [];
        for (const k of Object.keys(CORAL_ON_ROCK) as CoralSpecies[]) {
          if (a.n[1] >= CORAL_ON_ROCK[k].up) table.push([k, CORAL_ON_ROCK[k].weight]);
        }
        if (table.length === 0) continue;
        const species = pickWeighted(table);
        const cvi = anyOf(bySpecies.get(species) as number[]);

        lp.set(a.p[0], a.p[1], a.p[2]).multiplyScalar(rs).applyQuaternion(rot);
        // Grows out of the face, but still reaches for the light: pure face-normal makes an
        // overhanging colony point at the sand, pure up makes it float off the rock.
        ln.set(a.n[0], a.n[1], a.n[2]).applyQuaternion(rot).lerp(up, 0.45).normalize();

        const cs = rs * CORAL_ON_ROCK[species].scale * rng.range(0.72, 1.15);
        const crot = new THREE.Quaternion().setFromUnitVectors(up, ln);
        yawQ.setFromAxisAngle(up, rng.range(0, Math.PI * 2));
        crot.multiply(yawQ);
        // Sunk along the face normal, not along world Y — on a sloping face a vertical sink
        // leaves the colony hovering off one edge.
        pushInstance(cvi, rx + lp.x - ln.x * 0.10 * cs, ry + lp.y - ln.y * 0.10 * cs,
                     rz + lp.z - ln.z * 0.10 * cs, crot, cs);
      }
    };

    /* ---- rock formations -----------------------------------------------------------
       A reef is not boulders dropped on a plain, it is one broken mass with coral living on
       it. So rocks go down in CLUSTERS whose offsets are deliberately well under a rock
       width: the blocks have to interpenetrate to fuse into a single low formation, exactly
       the way the generator stacks convex chunks inside one rock.

       Broad rather than tall is a placement property, not a scale one — the instance matrix
       has to stay uniformly scaled, because the vertex shader transforms normals by the model
       matrix directly and a squashed Y would tilt the shading off every facet. Breadth comes
       from spreading more blocks sideways and letting the outer ones sit lower. */
    const formations: Array<{ x: number; z: number; r: number }> = [];
    const wantForms = 2 + rng.int(0, 3);
    for (let t = 0; t < 900 && formations.length < wantForms; t++) {
      const x = x0 + rng.range(0, CELL);
      const z = rng.range(Z_MIN, Z_MAX);
      if (reefMask(x, z) < REEF_ON) continue;
      const r = rng.range(3.4, 8.5);
      let ok = true;
      for (const f of formations) {
        if (Math.hypot(f.x - x, f.z - z) < (f.r + r) * 0.72) { ok = false; break; }
      }
      if (!ok) continue;
      formations.push({ x, z, r });
    }

    for (const F of formations) {
      const n = 4 + rng.int(0, 4);
      const base = rng.range(1.25, 2.30);
      for (let i = 0; i < n; i++) {
        // The first block is the core and sits biggest; the rest shoulder outward and taper,
        // which is what gives a formation a profile instead of a plateau.
        const a = rng.range(0, Math.PI * 2);
        const d = i === 0 ? 0 : F.r * (0.20 + 0.62 * Math.sqrt(rng.next()));
        const x = F.x + Math.cos(a) * d;
        const z = F.z + Math.sin(a) * d;
        if (reefMask(x, z) < 0.30) continue;
        const s = base * (i === 0 ? 1 : rng.range(0.42, 0.86) * (1 - 0.35 * (d / F.r)));
        const vi = anyOf(rockVi);
        const r = seat(vi, x, z, s);
        colonise(vi, x, z, s, r.rot, r.y);
      }
    }

    /* ---- lone stone ----------------------------------------------------------------
       Sparse now that the formations carry the bulk. A boulder out on the open lagoon sand is
       worth keeping: it says the sand is a veneer over the same rock, not a separate world.
       Nothing in the surf zone, where it would be ground to shingle. */
    for (let gx = x0; gx < x0 + CELL; gx += 11) {
      for (let gz = Z_MIN; gz < Z_MAX; gz += 11) {
        const x = gx + rng.range(0, 11);
        const z = gz + rng.range(0, 11);
        if (waterDepth(x, z) < 2.5) continue;
        const mk = reefMask(x, z);
        if (rng.next() > 0.07 + 0.30 * mk) continue;
        const s = rng.range(0.75, mk > 0.5 ? 1.7 : 1.15);
        const vi = anyOf(rockVi);
        const r = seat(vi, x, z, s);
        if (mk > 0.35) colonise(vi, x, z, s, r.rot, r.y);
      }
    }

    /* ---- coral stands on bare rock --------------------------------------------------
       A reef is zonal and largely monospecific: a thicket of one branching species, then a
       field of plates, then massive heads. Drawing a random form for every colony gives
       confetti on a floor no matter how the density is tuned. So a stand is founded on a
       patch of rock, given ONE variant and ONE size class, and then packed.

       A stand picks a variant index rather than a species deliberately: each variant carries
       its own baked palette, so one variant per stand buys a coherent colour for the whole
       thicket without a per-instance colour attribute. */
    const standWeight: Array<[CoralSpecies, number]> = [
      ['branching', 1.35], ['table', 1.00], ['brain', 0.85],
      ['massive', 0.95], ['foliose', 0.45], ['fan', 0.35],
    ];
    const stands: Array<{ x: number; z: number; r: number; vi: number }> = [];
    const wantStands = 2 + rng.int(0, 3);
    for (let t = 0; t < 900 && stands.length < wantStands; t++) {
      const x = x0 + rng.range(0, CELL);
      const z = rng.range(Z_MIN, Z_MAX);
      if (reefMask(x, z) < REEF_ON) continue;
      const r = rng.range(3.0, 7.0);
      let ok = true;
      for (const st of stands) {
        if (Math.hypot(st.x - x, st.z - z) < (st.r + r) * 0.55) { ok = false; break; }
      }
      // keep the flat thickets clear of the formations, which have their own coral
      for (const f of formations) {
        if (ok && Math.hypot(f.x - x, f.z - z) < f.r + r * 0.5) ok = false;
      }
      if (!ok) continue;
      stands.push({ x, z, r, vi: anyOf(bySpecies.get(pickWeighted(standWeight)) as number[]) });
    }

    for (const st of stands) {
      const K = CORAL_STAND[this.variants[st.vi].coralKind as CoralSpecies];
      const base = rng.range(K.scale[0], K.scale[1]);
      for (let gx = st.x - st.r; gx <= st.x + st.r; gx += K.spacing) {
        for (let gz = st.z - st.r; gz <= st.z + st.r; gz += K.spacing) {
          const x = gx + rng.range(-0.55, 0.55) * K.spacing;
          const z = gz + rng.range(-0.55, 0.55) * K.spacing;
          const d = Math.hypot(x - st.x, z - st.z);
          if (d > st.r) continue;
          // dense in the middle, thinning at the margin so neighbouring stands interleave
          // instead of reading as visible discs
          if (rng.next() > (1 - sstep(st.r * 0.5, st.r * 1.02, d)) * K.density) continue;
          if (reefMask(x, z) < 0.42) continue;
          seat(st.vi, x, z, base * rng.range(0.82, 1.18));
        }
      }
    }

    return out;
  }

  dispose(): void {
    this.group.removeFromParent();
    for (const m of this.meshes) m.dispose();
    for (const v of this.variants) v.geometry.dispose();
    for (const mat of this.materials) mat.dispose();
    this.meshes.length = 0;
    this.variants.length = 0;
    this.materials.length = 0;
    this.cells.clear();
  }
}
